import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  useTheme,
  Paper,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Grid,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import TeacherDashboardAnalytics from './TeacherDashboardAnalytics';
import AnalyticsDashboard from '../../components/analytics/AnalyticsDashboard';
import { getCurrentSubscription } from '../../services/subscriptionService';
import { useAuth } from '../../contexts/AuthContext';
import { TEACHERS_STUDENTS } from '../../config/Config';
import api from '../../services/api';
import { useLocation, Link } from 'react-router-dom';

// Tab Panel component for accessibility
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`teacher-analytics-tabpanel-${index}`}
      aria-labelledby={`teacher-analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TeacherAnalyticsWrapper = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [demoMode, setDemoMode] = useState(false);
  const [hasAnalyticsAccess, setHasAnalyticsAccess] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Student selection state for Student Specific tab
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  
  // Date and topic state for student analytics
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date;
  });
  const [toDate, setToDate] = useState(() => new Date());
  const [selectedTopic, setSelectedTopic] = useState('all');

  const checkSubscriptionAccess = useCallback(async () => {
    try {
      setSubscriptionLoading(true);
      
      // If demo mode is active, grant access immediately
      if (demoMode) {
        setHasAnalyticsAccess(true);
        setSubscriptionLoading(false);
        return;
      }
      
      const subscription = await getCurrentSubscription();
      
      // Check if user has teacher_pro plan
      const hasAccess = subscription && subscription.plan_code === 'teacher_pro';
      setHasAnalyticsAccess(hasAccess);
    } catch (error) {
      console.error('Error checking subscription access:', error);
      setHasAnalyticsAccess(false);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [demoMode]);

  // Fetch available students for Student Specific tab
  const fetchAvailableStudents = useCallback(async () => {
    if (!user || user.role !== 'teacher') {
      return;
    }

    try {
      setStudentsLoading(true);
      setStudentsError(null);
      
      const response = await api.get(TEACHERS_STUDENTS);
      const raw = Array.isArray(response.data) ? response.data : (response.data.results || response.data.students || []);
      
      // Transform and filter students
      const transformedStudents = raw
        .filter(r => r.is_active && r.is_accepted)
        .map(r => {
          const studentName = r.student_name;
          let firstName = '';
          let lastName = '';
          
          if (studentName && typeof studentName === 'string' && studentName.trim()) {
            const nameParts = studentName.trim().split(' ');
            firstName = nameParts[0] || '';
            lastName = nameParts.slice(1).join(' ') || '';
          }
          
          return {
            id: r.id,
            student_id: r.student,
            name: studentName || r.student_email || 'Unknown Student',
            email: r.student_email || '',
            subject: r.subject_name || r.subject?.name || 'No Subject'
          };
        });
      
      setAvailableStudents(transformedStudents);
      
      // Auto-select first student if none selected
      if (!selectedStudentId && transformedStudents.length > 0) {
        setSelectedStudentId(transformedStudents[0].student_id);
      }
      
      // If demo mode is enabled, auto-select demo user
      if (demoMode && transformedStudents.length > 0) {
        // Find demo user or use first available student
        const demoUser = transformedStudents.find(s => 
          s.email.toLowerCase().includes('demo') || 
          s.name.toLowerCase().includes('demo')
        ) || transformedStudents[0];
        setSelectedStudentId(demoUser.student_id);
      } else if (demoMode && transformedStudents.length === 0) {
        // If no students available in demo mode, create a demo student
        const demoStudent = {
          id: 'demo-student',
          student_id: 'demo@student.com',
          name: 'Demo Student',
          email: 'demo@student.com',
          subject: 'Demo Subject'
        };
        setAvailableStudents([demoStudent]);
        setSelectedStudentId(demoStudent.student_id);
      }
    } catch (err) {
      console.error('Error fetching available students:', err);
      setStudentsError('Failed to fetch students');
    } finally {
      setStudentsLoading(false);
    }
  }, [user, selectedStudentId, demoMode]);

  // Fetch analytics data for selected student
  const fetchStudentAnalytics = useCallback(async () => {
    if (!selectedStudentId) {
      setAnalyticsData(null);
      return;
    }

    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      
      // Use demo mode if enabled, otherwise use selected student
      const effectiveStudentId = demoMode ? 'demo@student.com' : selectedStudentId;
      
      const analyticsService = (await import('../../services/analyticsService')).default;
      const data = await analyticsService.getStudentDashboard(
        effectiveStudentId,
        fromDate,
        toDate,
        null,
        null,
        demoMode
      );
      
      setAnalyticsData(data);
    } catch (err) {
      setAnalyticsError(err.message || 'Failed to fetch analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [selectedStudentId, fromDate, toDate, demoMode]);

  useEffect(() => {
    checkSubscriptionAccess();
  }, [checkSubscriptionAccess]);

  // Check for demo mode in URL parameters and auto-enable if present
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlDemoMode = urlParams.get('demo');
    
    if (urlDemoMode === 'true') {
      setDemoMode(true);
      setHasAnalyticsAccess(true);
      
      // Remove the demo parameter from URL to avoid re-triggering
      urlParams.delete('demo');
      const newUrl = urlParams.toString() ? `${location.pathname}?${urlParams.toString()}` : location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);

  // Fetch available students when component mounts
  useEffect(() => {
    fetchAvailableStudents();
  }, [fetchAvailableStudents]);

  // Re-fetch students when demo mode changes
  useEffect(() => {
    if (demoMode) {
      fetchAvailableStudents();
    }
  }, [demoMode, fetchAvailableStudents]);

  // Re-fetch students when demo mode changes (immediate)
  useEffect(() => {
    if (demoMode) {
      fetchAvailableStudents();
    }
  }, [demoMode]);

  // Re-check subscription access when demo mode changes
  useEffect(() => {
    checkSubscriptionAccess();
  }, [demoMode, checkSubscriptionAccess]);

  // Fetch student analytics when student selection or filters change
  useEffect(() => {
    if (activeTab === 1 && selectedStudentId) {
      fetchStudentAnalytics();
    }
  }, [activeTab, selectedStudentId, fromDate, toDate, selectedTopic, demoMode, fetchStudentAnalytics]);

  // Re-fetch student analytics when demo mode changes
  useEffect(() => {
    if (activeTab === 1 && selectedStudentId && demoMode) {
      fetchStudentAnalytics();
    }
  }, [demoMode, activeTab, selectedStudentId, fetchStudentAnalytics]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get all available topics for the filter dropdown
  const getAvailableTopics = () => {
    if (!analyticsData?.topic_difficulty_breakdown?.topic_mastery) {
      return [];
    }
    const data = analyticsData.topic_difficulty_breakdown.topic_mastery;
    return [...new Set(data.map(item => item.topic))].sort();
  };

  const availableTopics = getAvailableTopics();

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 4 }, mb: 6, px: { xs: 1.5, sm: 2 }, overflowX: 'hidden', minWidth: 0 }}>
        {/* Header */}
        <Box mb={4}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
                Teacher Analytics Dashboard
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Comprehensive insights into class performance and individual student progress
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Monitor overall class performance and dive deep into individual student analytics to provide personalized support.
              </Typography>
            </Box>
            
            {/* Demo Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={demoMode}
                  onChange={(e) => {
                    setDemoMode(e.target.checked);
                    if (e.target.checked) {
                      // When demo mode is enabled, allow access to analytics
                      setHasAnalyticsAccess(true);
                    } else {
                      // When demo mode is disabled, recheck subscription
                      checkSubscriptionAccess();
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    Demo Mode
                  </Typography>
                  {demoMode && (
                    <Chip 
                      label="DEMO" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  )}
                </Box>
              }
            />
          </Box>
          
                     {/* Subscription Status */}
           {!demoMode && !hasAnalyticsAccess && (
             <Alert severity="warning" sx={{ mb: 2 }}>
               <Typography variant="body2">
                 <strong>Limited Access:</strong> Upgrade to Teacher Pro to access your class analytics.
               </Typography>
             </Alert>
           )}
          
          {/* Demo Mode Notice */}
          {demoMode && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Demo Mode Active:</strong> You are now viewing sample data for demonstration purposes. 
                Toggle off to view your actual class performance data.
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Tab Navigation */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="teacher analytics tabs"
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: { xs: 48, sm: 64 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 500,
                textTransform: 'none'
              }
            }}
          >
            <Tab 
              label="Teacher Dashboard" 
              id="teacher-analytics-tab-0"
              aria-controls="teacher-analytics-tabpanel-0"
            />
            <Tab 
              label="Student Specific" 
              id="teacher-analytics-tab-1"
              aria-controls="teacher-analytics-tabpanel-1"
            />
          </Tabs>
        </Paper>

        {/* Loading State */}
        {subscriptionLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
            <Typography>Loading analytics...</Typography>
          </Box>
        )}

        {/* Tab Panels */}
        {!subscriptionLoading && (
          <>
                                     <TabPanel value={activeTab} index={0}>
              <TeacherDashboardAnalytics demoMode={demoMode} />
            </TabPanel>
            
                         <TabPanel value={activeTab} index={1}>
               {/* Check if teacher has access to student analytics */}
               {!demoMode && !hasAnalyticsAccess ? (
                 <Box display="flex" flexDirection="column" alignItems="center" textAlign="center" py={8}>
                   <Typography variant="h4" component="h1" gutterBottom color="primary">
                     Student Analytics Access Restricted
                   </Typography>
                   <Typography variant="h6" gutterBottom color="text.secondary" sx={{ mb: 3 }}>
                     Upgrade to Teacher Pro plan to access individual student insights
                   </Typography>
                   <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
                     Get detailed individual student performance analytics, topic mastery insights, and personalized recommendations 
                     to provide targeted support for each student.
                   </Typography>
                   
                   <Box sx={{ mb: 4 }}>
                     <Button
                       variant="contained"
                       color="primary"
                       size="large"
                       component={Link}
                       to="/profile"
                       sx={{ mr: 2 }}
                     >
                       Go to Profile → My Subscription
                     </Button>
                     <Button
                       variant="outlined"
                       color="primary"
                       size="large"
                       onClick={() => {
                         setDemoMode(true);
                         setHasAnalyticsAccess(true);
                       }}
                     >
                       Try Demo Mode
                     </Button>
                   </Box>

                   <Alert severity="info" sx={{ maxWidth: 600 }}>
                     <Typography variant="body2">
                       <strong>Teacher Pro Plan:</strong> Unlock individual student analytics including performance metrics, 
                       topic mastery breakdowns, and progress tracking to enhance your teaching effectiveness.
                     </Typography>
                   </Alert>
                 </Box>
               ) : (
                 <>
                   {/* Student Selection Filter */}
                   <Box mb={3}>
                     <Typography variant="h6" gutterBottom>
                       Select Student
                     </Typography>
                     
                     {demoMode ? (
                       <Alert severity="info" sx={{ mb: 2 }}>
                         <Typography variant="body2">
                           <strong>Demo Mode Active:</strong> Student selection is disabled. You are viewing demo data for demonstration purposes.
                         </Typography>
                       </Alert>
                     ) : (
                       <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                         Choose a student to view their analytics
                       </Typography>
                     )}
                     
                     {studentsLoading ? (
                       <CircularProgress size={20} />
                     ) : studentsError ? (
                       <Alert severity="error" sx={{ mb: 2 }}>{studentsError}</Alert>
                     ) : availableStudents.length > 0 ? (
                       <FormControl fullWidth sx={{ maxWidth: 400 }}>
                         <InputLabel id="student-select-label">Select Student by Email</InputLabel>
                         <Select
                           labelId="student-select-label"
                           value={selectedStudentId || ''}
                           label="Select Student by Email"
                           onChange={(e) => !demoMode && setSelectedStudentId(e.target.value)}
                           disabled={demoMode}
                           sx={{ 
                             opacity: demoMode ? 0.6 : 1,
                             '& .MuiSelect-select': {
                               display: 'flex',
                               alignItems: 'center',
                               gap: 1
                             }
                           }}
                         >
                           {availableStudents.map((student) => (
                             <MenuItem key={student.student_id} value={student.student_id}>
                               <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                 <Typography variant="body2" fontWeight="medium">
                                   {student.email}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary">
                                   {student.name} • {student.subject}
                                 </Typography>
                               </Box>
                             </MenuItem>
                           ))}
                         </Select>
                       </FormControl>
                     ) : (
                       <Alert severity="info">
                         No students available. Add students to your subjects to view their analytics.
                       </Alert>
                     )}
                   </Box>

                   {/* Student Analytics Dashboard */}
                   {selectedStudentId && (
                     demoMode && (
                       <Alert severity="info" sx={{ mb: 2 }}>
                         <Typography variant="body2">
                           <strong>Demo Mode:</strong> Viewing analytics for <strong>Demo Student</strong>
                         </Typography>
                       </Alert>
                     )
                   )}
                   {selectedStudentId && (
                     analyticsLoading ? (
                       <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                         <CircularProgress />
                       </Box>
                     ) : analyticsError ? (
                       <Alert severity="error" sx={{ mb: 2 }}>{analyticsError}</Alert>
                     ) : analyticsData ? (
                       <AnalyticsDashboard
                         analyticsData={analyticsData}
                         fromDate={fromDate}
                         toDate={toDate}
                         selectedTopic={selectedTopic}
                         demoMode={demoMode}
                         onFromDateChange={setFromDate}
                         onToDateChange={setToDate}
                         onTopicChange={setSelectedTopic}
                         availableTopics={availableTopics}
                       />
                     ) : (
                       <Alert severity="info">
                         Select a student to view their analytics data.
                       </Alert>
                     )
                   )}
                 </>
               )}
             </TabPanel>
          </>
        )}
      </Container>
  );
};

export default TeacherAnalyticsWrapper;
